/** Every way the console writes money and ratios, in one file (plan 1-3).
 *
 *  Dollars always carry two decimals: most days earn under a dollar, so
 *  "$0.43" has to stay "$0.43" and not become "$0". A loss is "−$1.23" with
 *  U+2212, never "-$1.23" or "($1.23)"; a gain has no "+". The short form
 *  ("$1.2k") is for chart axes only. Numbers are drawn in the text colour —
 *  whether a figure is good or bad is said by a status pill, not by tinting
 *  the figure. */

export const MINUS = '−'

const USD = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

function finite(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

/** "$1,234.56", "−$1.23", "$0.00"; "—" for no number. */
export function usd(v: number | null | undefined): string {
  if (!finite(v)) return '—'
  // -0.004 rounds to "0.00": no minus on a zero.
  const abs = USD.format(Math.abs(v))
  return v < 0 && abs !== '0.00' ? `${MINUS}$${abs}` : `$${abs}`
}

/** The axis form: "$1.2k" from a thousand, "$12" from ten, else "$1.50". */
export function usdAxis(v: number | null | undefined): string {
  if (!finite(v)) return ''
  const a = Math.abs(v)
  const sign = v < 0 ? MINUS : ''
  if (a >= 1000) return `${sign}$${(a / 1000).toFixed(a >= 10_000 ? 0 : 1)}k`
  if (a >= 10) return `${sign}$${Math.round(a)}`
  return `${sign}$${a.toFixed(2)}`
}

/** A count with thousands separators: "12,345". */
export function count(n: number | null | undefined): string {
  return finite(n) ? INT.format(n).replace('-', MINUS) : '—'
}

/** A 0–1 fraction (CTR, match rate, share) as a percent: "12.3%". */
export function percent(fraction: number | null | undefined, digits = 1): string {
  if (!finite(fraction)) return '—'
  return `${(fraction * 100).toFixed(digits)}%`.replace('-', MINUS)
}

/** ROAS as a multiple, "2.31×"; "—" when nothing was spent (there is no
 *  ratio — the API sends 0). */
export function roas(r: number | null | undefined, cost: number | null | undefined): string {
  if (!finite(r) || !finite(cost) || cost <= 0) return '—'
  return `${r.toFixed(2)}×`
}

/** The change from `base` to `now` as a fraction, or null when there is no
 *  base to compare with (0 or missing): "—", never "+∞%". */
export function change(now: number | null | undefined, base: number | null | undefined): number | null {
  if (!finite(now) || !finite(base) || base === 0) return null
  return (now - base) / Math.abs(base)
}

/** An amount in the Ads account currency: "₩5,000" (KRW has no decimals),
 *  "€4.50". Falls back to "5,000 XYZ" for a code Intl does not know. */
export function localMoney(v: number | null | undefined, currency: string): string {
  if (!finite(v)) return '—'
  try {
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .format(v)
      .replace('-', MINUS)
  } catch {
    return `${v.toLocaleString('en-US')} ${currency}`
  }
}

/** "≈ ₩6,628/일 (1 USD = ₩1,325.57, 10-21)" for a dollar amount typed into a
 *  budget form, rounded to the currency's budget step like the server does.
 *  "" for a USD account, no rate or no amount. */
export function localBudgetPreview(amountUSD: number, fx: { currency: string; local_per_usd: number; unit_local: number; date?: string } | undefined): string {
  if (!fx || fx.currency === 'USD' || !(fx.local_per_usd > 0) || !finite(amountUSD) || amountUSD <= 0) return ''
  const unit = fx.unit_local > 0 ? fx.unit_local : 1
  const local = Math.max(1, Math.round((amountUSD * fx.local_per_usd) / unit)) * unit
  const rate = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 2 }).format(fx.local_per_usd)
  const sym = localMoney(0, fx.currency).replace(/[\d.,\s]/g, '')
  return `≈ ${localMoney(local, fx.currency)}/일 (1 USD = ${sym}${rate}${fx.date ? `, ${fx.date}` : ''})`
}

const WON = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 })
const RATE = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 2 })

/** A dollar amount in won at `krwPerUSD`, to the whole won: "₩16,350",
 *  "−₩1,630"; "—" for no number or no rate. */
export function krw(v: number | null | undefined, krwPerUSD: number | null | undefined): string {
  if (!finite(v) || !finite(krwPerUSD) || krwPerUSD <= 0) return '—'
  const won = Math.round(v * krwPerUSD)
  const abs = WON.format(Math.abs(won))
  return won < 0 ? `${MINUS}₩${abs}` : `₩${abs}`
}

/** The rate the won figures use: "1 USD = ₩1,325.57 (2026-10-21)". */
export function krwRateLabel(krwPerUSD: number, date?: string): string {
  return `1 USD = ₩${RATE.format(krwPerUSD)}${date ? ` (${date})` : ''}`
}
